import React, {useState, useEffect} from "react";
import {useSelector} from "react-redux";

interface FloatingChatbotButtonProps {
  onToggleChatbot: () => void;
}

const FloatingChatbotButton: React.FC<FloatingChatbotButtonProps> = ({ onToggleChatbot }) => {

  const {
    contextInfo
  } = useSelector((state: any) => state.chatMessages)

  const [shouldAnimate, setShouldAnimate] = useState(true);

  // Check localStorage on mount to determine if animation should be shown
  useEffect(() => {
    const animationDisabled = localStorage.getItem("chatbotAnimationDisabled");
    setShouldAnimate(animationDisabled !== "true");
  }, []);

  return (
    <button
      onClick={onToggleChatbot}
      className={`fixed right-4 sm:right-6 md:right-10 h-16 w-16 sm:h-18 sm:w-18 md:h-20 md:w-20 bottom-6 sm:bottom-8 md:bottom-10 z-[9998] cursor-pointer ai-chatbot`}
    >
      {/* Modern AI Chat Button with Animation */}
      <div className={`relative group ${shouldAnimate ? 'animate-floatBounce' : ''}`}>
        {/* Main button with logo and pulsing effect */}
        <div className={`w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 rounded-full bg-gradient-to-tr from-blue-500 to-blue-700 opacity-100 flex items-center justify-center shadow-lg relative overflow-hidden transition-all duration-300 ${shouldAnimate ? 'animate-slowPulse' : ''}`}>
          {/* Gradient background with animated rotation */}
          <div className="absolute inset-1 bg-gradient-to-tr from-blue-500 to-blue-700 opacity-100"></div>

          {/* Circular scanning effect - more subtle */}
          {shouldAnimate && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-24 h-24 rounded-full border border-blue-300/50 animate-spin-slow"></div>
            </div>
          )}

          {/* Logo with hover effect - larger and brighter */}
          <img
            src="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/cadencelogo.webp"
            className="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 z-10 transform transition-transform group-hover:scale-110 brightness-110 contrast-110"
            alt="Cadence AI"
          />
        </div>

        {/* Label that appears on hover with animated entrance */}
        <div className="absolute -top-10 right-0 bg-white dark:bg-gray-800 px-3 py-1 rounded-full shadow-md text-blue-800 dark:text-blue-400 text-sm font-medium opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-1 group-hover:translate-y-0 whitespace-nowrap">
          {contextInfo.chatButtonText}
          <div className="absolute bottom-[-6px] right-5 w-3 h-3 bg-white dark:bg-gray-800 transform rotate-45"></div>
        </div>

        {/* Outer glow effect - appears only outside the button */}
        {shouldAnimate && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[130%] h-[130%] rounded-full bg-blue-400/70 dark:bg-blue-400/25 blur-md -z-10 animate-pulse-slow pointer-events-none"></div>
        )}

        {/* Ring pulse effect */}
        <div className="absolute -inset-2 rounded-full border border-blue-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
      </div>
    </button>
  );
}

export default FloatingChatbotButton;